import React, { RefObject, useState, useEffect } from 'react';
import { ChatInputArea, Document } from './index';
import { ExecutionDocument } from '../../../../../shared/services/documentService';
import { KnowledgeBaseWithDocuments } from '../../../../../types/navigationTypes';
import type { EnabledConnectors } from '../../../types/chatTypes';
import { cn } from "@/lib/utils";

interface ChatFooterProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSend: () => void;
  onStop?: () => void;
  onKeyPress: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  loading?: boolean;
  disabled?: boolean;
  inputRef?: RefObject<HTMLTextAreaElement>;
  placeholder?: string;
  initialDocuments?: Document[];
  documentsEnabled?: boolean;
  onDocumentsChange?: (documents: Document[]) => void;
  knowledgeBaseDocuments?: ExecutionDocument[];
  isLoadingKbDocs?: boolean;
  onLoadDocuments?: () => void;
  onRefetchDocuments?: () => void;
  isAgentMode?: boolean;
  onToggleAgentMode?: () => void;
  isTestMode?: boolean;
  onToggleTestMode?: () => void;
  isWebSearchMode?: boolean;
  onToggleWebSearchMode?: () => void;
  knowledgeBases?: KnowledgeBaseWithDocuments[];
  onUploadSuccess?: () => void;
  onOpenConnectors?: () => void;
  enabledConnectors?: EnabledConnectors;
  availableConnectors?: EnabledConnectors;
  workpillExecutionId?: string | null;
}

export const ChatFooter: React.FC<ChatFooterProps> = ({
  value,
  onChange,
  onSend,
  onStop,
  onKeyPress,
  loading = false,
  disabled = false,
  inputRef,
  placeholder = "Type your message...",
  initialDocuments = [],
  documentsEnabled = false,
  onDocumentsChange,
  knowledgeBaseDocuments = [],
  isLoadingKbDocs = false,
  onLoadDocuments,
  onRefetchDocuments,
  isAgentMode = false,
  onToggleAgentMode,
  isTestMode = false,
  onToggleTestMode,
  isWebSearchMode = false,
  onToggleWebSearchMode,
  knowledgeBases = [],
  onUploadSuccess,
  onOpenConnectors,
  enabledConnectors,
  availableConnectors,
  workpillExecutionId,
}) => {
  const [documents, setDocuments] = useState<Document[]>(initialDocuments);

  useEffect(() => {
    if (onDocumentsChange) {
      onDocumentsChange(documents);
    }
  }, [documents, onDocumentsChange]);

  const handleAttachKnowledgeBaseDocument = (doc: ExecutionDocument, isCategory: boolean = false) => {
    if (!documentsEnabled) return;

    const isKb =
      isCategory ||
      doc.type === 'knowledge_base' ||
      (typeof doc.id === 'string' && doc.id.startsWith('kb-'));

    let docType: Document['type'] = 'pdf';
    if (isKb) {
      docType = 'knowledge_base';
    } else {
      const name = doc.name.toLowerCase();
      if (name.includes('template') || name.includes('plantilla')) {
        docType = 'template';
      } else if (name.endsWith('.pdf')) {
        docType = 'pdf';
      } else if (name.endsWith('.doc') || name.endsWith('.docx')) {
        docType = 'doc';
      } else if (name.endsWith('.txt')) {
        docType = 'txt';
      }
    }

    const newDoc: Document = {
      id: doc.id,
      name: doc.name || `Document ${doc.id}`,
      type: docType,
      isCategory: isKb || isCategory,
    };
    
    if (!documents.some(existingDoc => existingDoc.id === newDoc.id)) {
      setDocuments(prev => [...prev, newDoc]);
    }
  };

  const handleRemoveDocument = (id: string) => {
    setDocuments(prev => prev.filter(doc => doc.id !== id));
  };

  return (
    <div className={cn(
      "bg-background py-1"
    )}>
      <ChatInputArea
        value={value}
        onChange={onChange}
        onSend={onSend}
        onStop={onStop}
        onKeyPress={onKeyPress}
        onAttachKnowledgeBaseDocument={handleAttachKnowledgeBaseDocument}
        documents={documents}
        onRemoveDocument={handleRemoveDocument}
        loading={loading}
        disabled={disabled}
        inputRef={inputRef}
        placeholder={placeholder}
        knowledgeBaseDocuments={knowledgeBaseDocuments}
        isLoadingKbDocs={isLoadingKbDocs}
        onLoadDocuments={onLoadDocuments}
        onRefetchDocuments={onRefetchDocuments}
        isAgentMode={isAgentMode}
        onToggleAgentMode={onToggleAgentMode}
        isTestMode={isTestMode}
        onToggleTestMode={onToggleTestMode}
        isWebSearchMode={isWebSearchMode}
        onToggleWebSearchMode={onToggleWebSearchMode}
        knowledgeBases={knowledgeBases}
        onUploadSuccess={onUploadSuccess}
        onOpenConnectors={onOpenConnectors}
        enabledConnectors={enabledConnectors}
        availableConnectors={availableConnectors}
        workpillExecutionId={workpillExecutionId}
      />
    </div>
  );
};
